<template>
  <!-- one class on the root; the host still puts its own Bootstrap `card` on this element -->
  <div v-show="orders.length > 0 || showWhenEmpty" class="orders" :class="{ 'border-primary': highlight }">
    <h5>
      {{ title }} <small v-if="subTitle">{{ subTitle }}</small>
    </h5>

    <input
      v-if="searchable"
      type="search"
      v-model="search"
      placeholder="št. naloga, zadeva ali opis ..."
      title="Išče po številki naloga, zadevi in opisu napake"
    />

    <p v-if="!orders.length">{{ emptyText }}</p>

    <article v-for="wo in displayedOrders" :key="wo.id" :class="{ linked: linkedOrderId === wo.id }">
      <header>
        <b v-if="wo.priority >= 1 && wo.priority <= 3" :data-priority="wo.priority">{{ wo.priority }}</b>
        <router-link
          v-if="actionTitle"
          :to="{ name: 'ServiceOrder', params: { workOrderId: wo.id } }"
          target="_blank"
          title="Odpri v novem zavihku"
          >{{ wo.orderNumber }}</router-link
        >
        <a v-else @click="selectOrderAsync(wo)" role="button">{{ wo.orderNumber }}</a>
        <OrderBadge :workOrder="wo" />
        <em v-if="wo.hadUncompletedCommission">Delni komision!</em>
        <span v-if="showOwner && wo.owner?.trim()">{{ wo.owner }}</span>
        <strong v-if="showFacility">{{ wo.facilityName }}</strong>
      </header>

      <p>
        {{ wo.subject }}
        <b v-if="wo.distanceKm != null">{{ wo.distanceKm?.toFixed(0) }} km</b>
      </p>

      <span v-if="actionTitle && linkedOrderId === wo.id">Povezan</span>
      <button v-else-if="actionTitle || (isAdmin && wo.status === 'Active')" type="button" @click="rowAction(wo)">
        {{ actionTitle || "Dodeli" }}
      </button>
    </article>

    <p v-if="paging.pages > 1">
      <router-link :to="{ name: 'Service', query: { facilityID, customerID, ownerID, statuses } }"
        >Več zadetkov</router-link
      >
      <router-link
        v-if="showLinkToMap"
        :to="{ name: 'ServiceOrdersMap', query: { facilityID, customerID, ownerID, statuses } }"
        >Več zadetkov na zemljevidu 🚩</router-link
      >
    </p>
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import {
  useAuth,
  authState,
  isAuthenticated,
  user,
  role,
  isAdmin,
  showMessageToWorker,
  currentWorkerId,
} from "../composition-api/useAuth"
import { DATETIME_FOR_LOCAL_INPUT, EMPTY_GUID } from "../utils/constants"
import { useWorkOrders } from "../composition-api/useWorkOrders"
import { assignmentsDone, openAssign } from "./AssignWorkOrder"

const SEARCH_DEBOUNCE_MS = 400

defineOptions({ name: "ServiceOrdersCard" })

const props = defineProps({
  showWhenEmpty: {
    type: Boolean,
    default: true,
  },
  // the host names the per-row action ("Poveži", "Dodeli", ...): the card only renders it,
  // reports the pick through "select" and opens the order number in a new tab. Left empty,
  // the card keeps its built-in admin "Dodeli" that opens the assign modal.
  actionTitle: {
    type: String,
    default: "",
  },
  // the order already linked to the record that hosts the picker - its row reads "Povezan"
  linkedOrderId: {
    type: String,
    default: "",
  },
  // offer a search box (order number, subject, fault description) that refills the list as you type
  searchable: {
    type: Boolean,
    default: false,
  },
  // draw the "this is where you pick" border (the host knows when the pick is expected)
  highlight: {
    type: Boolean,
    default: false,
  },
  statuses: {
    type: String,
    default: "",
  },
  ownerID: {
    type: String,
    default: EMPTY_GUID,
  },
  facilityID: {
    type: String,
    default: EMPTY_GUID,
  },
  deviceID: {
    type: String,
    default: EMPTY_GUID,
  },
  projectID: {
    type: String,
    default: EMPTY_GUID,
  },
  customerID: {
    type: String,
    default: EMPTY_GUID,
  },
  isSpecial: {},
  title: {
    type: String,
  },
  subTitle: {
    type: String,
  },
  itemsPerPage: {
    type: Number,
    default: 25,
  },
  showLinkToMap: {
    type: Boolean,
    default: true,
  },
  excludeId: {
    type: String,
    default: EMPTY_GUID,
  },
  showFacility: {
    type: Boolean,
    default: true,
  },
  showOwner: {
    type: Boolean,
    default: true,
  },
  sortBy: {
    type: String,
    default: null,
  },
  showDisplayOrder: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(["assigned", "select"])

const { resetPaging, filters, sortBy: sortByRef, orders, paging, location, selectOrderAsync } = useWorkOrders("Service")

// live search - the debounce keeps a burst of keystrokes down to one request
const search = ref("")
let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => (filters.searchTerm = search.value.trim()), SEARCH_DEBOUNCE_MS)
})

// what an empty list means: a search that missed, a facility without SDNs, or no orders at all
const emptyText = computed(() => {
  if (props.searchable && search.value.trim()) return "Ni zadetkov."
  return props.actionTitle ? "Objekt nima SDN." : "Ni nalogov."
})

// the order a host excludes (the one it already shows as linked) is not rendered here
const displayedOrders = computed(() => orders.value.filter((o) => o.id != props.excludeId))

// the row action: the host's when it named one (it gets the pick through "select"), otherwise
// the built-in "Dodeli" that opens the app's assign modal (App.js)
const rowAction = (wo) => {
  if (props.actionTitle) {
    emit("select", wo)
    return
  }
  openAssign(wo)
}

// an assignment landed somewhere in the app - the owner shown in this list may be stale now
watch(assignmentsDone, () => {
  resetPaging()
  emit("assigned")
})

watch(
  () => [
    props.itemsPerPage,
    props.statuses,
    props.ownerID,
    props.facilityID,
    props.deviceID,
    props.customerID,
    props.isSpecial,
  ],
  async () => await loadDataAsync(),
)

onMounted(async () => await loadDataAsync())

const loadDataAsync = async () => {
  paging.value.itemsPerPage = props.itemsPerPage
  filters.status = props.statuses
  filters.ownerID = props.ownerID
  filters.facility = props.facilityID
  filters.device = props.deviceID
  filters.customer = props.customerID
  filters.isSpecial = props.isSpecial
  filters.projectID = props.projectID
  sortByRef.value = props.sortBy
  resetPaging()
}

defineExpose({ loadDataAsync })
</script>

<style scoped>
/* Nested CSS, no framework: the markup stays semantic (h5 / input / article / header / p / button) and
   only the root carries a class, so hosts can keep their own Bootstrap `card` wrapper untouched. */
.orders {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.85rem 0.9rem 0.95rem;
  /* a touch of darkness so the white order boxes read as boxes */
  background: #f4f5f7;

  & > h5 {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.15rem 0.5rem;
    margin: 0;
    font-size: 1.02rem;
    line-height: 1.3;
    color: #1b2733;

    & > small {
      font-size: 0.78rem;
      font-weight: 400;
      color: #7c8794;
    }
  }

  & > input[type="search"] {
    font: inherit;
    font-size: 0.82rem;
    padding: 0.32rem 0.55rem;
    border: 1px solid #e2e5e9;
    border-radius: 0.45rem;
    background: #fff;
    transition:
      border-color 0.15s,
      box-shadow 0.15s,
      background-color 0.15s;

    &::placeholder {
      color: #a8b0b8;
    }

    &:focus {
      outline: 0;
      border-color: #9dbdf7;
      background: #fff;
      box-shadow: 0 0 0 0.15rem rgb(13 110 253 / 12%);
    }
  }

  /* card-level things: the empty state and the "more results" line */
  & > p {
    margin: 0;
    font-size: 0.8rem;
    color: #6b7480;

    & > a {
      color: #4d5b68;
      text-decoration: underline;
      text-decoration-color: #c9d1d9;
      text-underline-offset: 0.15em;

      &:hover {
        color: #0d6efd;
        text-decoration-color: currentcolor;
      }

      & + a {
        margin-left: 0.75rem;
      }
    }
  }

  /* every order is one box: head row with the action pinned right, subject row underneath.
     the head wraps instead of pushing the action around, so the button stays right at any width. */
  & > article {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "head action"
      "subject subject";
    column-gap: 0.6rem;
    row-gap: 0.3rem;
    padding: 0.5rem 0.6rem 0.55rem 0.55rem;
    border: 1px solid #e9ecf0;
    border-left: 3px solid #dde2e7;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 1px 1px rgb(16 24 40 / 4%);
    transition:
      border-color 0.15s,
      box-shadow 0.15s;

    &:hover {
      border-color: #d3d9df;
      border-left-color: #a9b3bd;
      box-shadow: 0 2px 5px rgb(16 24 40 / 8%);
    }

    /* the order this card is offering to link to */
    &.linked {
      border-color: #bcd6fb;
      border-left-color: #0d6efd;
      background: #f7fbff;
    }

    & > header {
      grid-area: head;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.3rem 0.4rem;
      min-width: 0;

      /* priority chip */
      & > b {
        min-width: 1.25rem;
        padding: 0.05rem 0.3rem;
        border-radius: 0.35rem;
        font-size: 0.7rem;
        font-weight: 700;
        text-align: center;
        color: #fff;
        background: #b3bbc3;

        &[data-priority="1"] {
          background: #d94b57;
        }

        &[data-priority="2"] {
          background: #e2883c;
        }

        &[data-priority="3"] {
          background: #3fa8bf;
        }
      }

      /* the order number */
      & > a {
        font-size: 0.88rem;
        font-weight: 600;
        color: #1b2733;
        text-decoration: none;

        &:hover {
          color: #0d6efd;
          text-decoration: underline;
        }
      }

      & > em {
        padding: 0.05rem 0.4rem;
        border-radius: 0.35rem;
        font-size: 0.68rem;
        font-style: normal;
        font-weight: 600;
        color: #7a5200;
        background: #fff1c9;
      }

      & > span,
      & > strong {
        min-width: 0;
        overflow: hidden;
        font-size: 0.78rem;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      & > span {
        color: #7c8794;
      }

      /* "Objekt X" - the facility the order belongs to */
      & > strong {
        font-weight: 600;
        color: #4d5b68;
      }

      /* the status badges come from the shared OrderBadge component - a touch lighter in here */
      & :deep(.badge) {
        padding: 0.18rem 0.4rem;
        border-radius: 0.35rem;
        font-size: 0.68rem;
        font-weight: 600;
        letter-spacing: 0.01em;
      }
    }

    /* the subject gets its own row: it is the one thing that can be long */
    & > p {
      grid-area: subject;
      margin: 0;
      overflow-wrap: anywhere;
      font-size: 0.8rem;
      line-height: 1.35;
      color: #5a6673;

      & > b {
        margin-left: 0.3rem;
        font-weight: 500;
        color: #98a1aa;
        white-space: nowrap;
      }
    }

    & > span,
    & > button {
      grid-area: action;
      align-self: start;
      justify-self: end;
    }

    & > span {
      padding-top: 0.15rem;
      font-size: 0.74rem;
      font-weight: 600;
      color: #2f7d3a;
      white-space: nowrap;

      &::after {
        content: " ✓";
      }
    }

    & > button {
      font: inherit;
      font-size: 0.74rem;
      font-weight: 500;
      line-height: 1.1;
      padding: 0.22rem 0.55rem;
      border: 1px solid #cfd6dd;
      border-radius: 0.4rem;
      background: #fff;
      color: #36404a;
      white-space: nowrap;
      cursor: pointer;
      transition:
        color 0.15s,
        background-color 0.15s,
        border-color 0.15s;

      &:hover {
        color: #fff;
        background: #0d6efd;
        border-color: #0d6efd;
      }

      &:focus-visible {
        outline: 0;
        box-shadow: 0 0 0 0.15rem rgb(13 110 253 / 22%);
      }

      &:active {
        transform: translateY(1px);
      }
    }
  }
}
</style>
